import { Inject, Injectable, PLATFORM_ID, WritableSignal, inject, signal } from '@angular/core';
import { Location, isPlatformBrowser } from '@angular/common';
import { catchError, finalize, of, tap, timeout } from 'rxjs';
import { LocalStorageService } from '../shared/local-storage.service';
import { LOCALSTORAGE_KEYS } from '../shared/constants/databases';
import { ApiService } from '../shared/api.service';
import { RequestHandlerParams } from '../shared/types/api-service.types';
import { ApiResponse } from '../shared/common/types';
import { AccountService } from '../shared/account.service';
import { map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { NotificationService } from '../shared/notification/notification.service';
import { KlaviyoService } from '../shared/klaviyo/klaviyo.service';
import { Session } from '../shared/types/session.type';
import { AttributionService } from '../shared/attribution/attribution.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private localStorageService = inject(LocalStorageService);
  private apiService = inject(ApiService);
  private accountService = inject(AccountService);
  #router = inject(Router);
  #notificationService = inject(NotificationService);
  #klaviyoService = inject(KlaviyoService);
  #attributionService = inject(AttributionService);


  endpoints = {
    login: `/login`,
    logout: `/logout`
  }

  isLoadingLogin: WritableSignal<boolean> = signal(false);

  constructor(
    private location: Location,
    @Inject(PLATFORM_ID) private platformId: any
  ) { }


  async login(email: string, password: string, hasExtendedSession: boolean = false) {
    this.isLoadingLogin.set(true)

    const params: RequestHandlerParams = {
      endpoint: this.endpoints.login,
      method: 'POST',
      body: this.#attributionService.withAttribution({ email, password, hasExtendedSession }),
      apiV3: true
    }

    this.localStorageService.set(LOCALSTORAGE_KEYS.NO_PASSWORD_USER_EMAIL, email);

    this.apiService.handleRequest<ApiResponse<any>>(params).pipe(
      tap(async (response: any) => {
        if (response.noPassword) {
          this.location.replaceState('/account-recovery/new-ecommerce');
          window.location.reload();
          return;
        }

        this.localStorageService.remove(LOCALSTORAGE_KEYS.NO_PASSWORD_USER_EMAIL);
        const { data } = response;
        this.localStorageService.set(LOCALSTORAGE_KEYS.SESSION, { ...data });

        await this.#klaviyoService.identiyUser();

        this.localStorageService.set(LOCALSTORAGE_KEYS.FROM_LOGIN, true);

        // Validate if exists a stored route to navigate after login:
        const routeToNavigateAfterLogin: string = this.localStorageService.get(LOCALSTORAGE_KEYS.ROUTE_TO_NAVIGATE_AFTER_LOGIN) || '/shop';
        this.localStorageService.remove(LOCALSTORAGE_KEYS.ROUTE_TO_NAVIGATE_AFTER_LOGIN);

        // // Get memebership permissions:
        this.accountService.getMembershipPermissions(routeToNavigateAfterLogin, true).subscribe();
      }),
      finalize(() => this.isLoadingLogin.set(false))
    ).subscribe();
  }

  /**
   * How long to wait for the server to end the session before signing out locally
   * anyway. The call is worth making -- it is what stops a token being replayed
   * after the customer has left -- but nobody should be held on the page because
   * the API is slow or down.
   */
  private readonly logoutTimeoutMs = 4000;

  /**
   * Ends the session on the server as well as in the browser.
   *
   * `POST /logout` answers 204 and invalidates the token it was called with, so a
   * copy taken from storage stops working the moment someone signs out. The browser
   * is then cleared whatever the call did: a request that fails, times out or never
   * leaves must never be the reason somebody stays signed in.
   */
  logout() {
    if (!isPlatformBrowser(this.platformId)) return;

    const session: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    const token = session?.accountInfo?.token;

    // No token means there is no server-side session to end, and asking anyway
    // answers 403 SESSION_MISSING -- which the error handler reads as an expired
    // session and acts on, clearing storage and reloading a second time.
    if (!token) {
      this.clearBrowserSession();
      return;
    }

    const params: RequestHandlerParams = {
      endpoint: this.endpoints.logout,
      method: 'POST',
      apiV3: true,
      // 204, so there is no body to wait for. The failure stays silent on purpose:
      // the customer asked to be signed out, not to be told the API was unreachable.
      isVoid: true,
      showErrorMessage: false,
      returnError: true
    };

    this.apiService.handleRequest<unknown>(params).pipe(
      timeout(this.logoutTimeoutMs),
      catchError(() => of(null)),
      // Runs on the 204, on an error and on the timeout alike.
      finalize(() => this.clearBrowserSession())
    ).subscribe();
  }

  private clearBrowserSession() {
    localStorage.clear();
    const cookiesToDelete = ['__kla_id'];

    cookiesToDelete.forEach(cookie => {
      document.cookie = `${cookie}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });

    this.reloadPage('/');
  }

  private reloadPage(route: string) {
    this.location.replaceState(route);
    window.location.reload();
  }

  loginAsAdmin(token: string) {
    this.localStorageService.clear();

    const params: RequestHandlerParams = {
      endpoint: '/login-admin',
      method: 'GET',
      apiV3: true,
      returnError: true,
      token
    };

    return this.apiService.handleRequest<ApiResponse<any>>(params).pipe(
      tap(async res => {
        if (res.error) {
          this.#notificationService.show({ text: res.message ?? 'There was an error trying to authenticate the request', type: 'error' })
          this.#router.navigate(['/']);
          return;
        }
        const { data } = res;
        this.localStorageService.set(LOCALSTORAGE_KEYS.SESSION, { ...data });
        await this.#klaviyoService.identiyUser();
        this.accountService.getMembershipPermissions('/shop').subscribe();
      })
    );
  }
}
